import React from 'react';
import moment from 'moment';
import { T, NUMERIC } from './orders.theme';
import { COPY, money } from './orders.copy';
import { CalendarIcon, ChevronDownIcon, DownloadIcon } from './OrdersIcons';
import { tintFor } from './PayAccountDialog';

/**
 * Total, discount and the three money slots, on the dark teal surface.
 *
 * The date range and the totals live in one card because the range is what
 * the totals MEAN. On white they read as the first row of the list rather
 * than the thing summarising it.
 *
 * Waiters never see this — they get {@link DateStrip} instead.
 */

const Slot = ({ label, amount, dot, currency }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <span style={{ width: 7, height: 7, borderRadius: T.radiusPill, background: dot }} />
      <span style={{ fontSize: 11.5, color: T.onHeaderSoft, fontWeight: 500 }}>{label}</span>
    </div>
    <span style={{ fontSize: 15, fontWeight: 700, color: T.onHeader, ...NUMERIC }}>
      {money(amount, currency)}
    </span>
  </div>
);

export function formatRange(range) {
  const start = moment(range.start, 'YYYY-MM-DD');
  const end = moment(range.end, 'YYYY-MM-DD');

  if (start.isSame(end, 'day')) {
    return start.isSame(moment(), 'day')
      ? `${COPY.today} · ${start.format('ddd D MMM YYYY')}`
      : start.format('ddd D MMM YYYY');
  }
  if (start.isSame(end, 'year')) {
    return start.isSame(end, 'month')
      ? `${start.format('D')} – ${end.format('D MMM YYYY')}`
      : `${start.format('D MMM')} – ${end.format('D MMM YYYY')}`;
  }
  return `${start.format('D MMM YYYY')} – ${end.format('D MMM YYYY')}`;
}

/**
 * One account's pill: where some of the cash landed, and how much.
 *
 * The dot borrows the colour the pay dialog gave that account, so the button
 * a cashier pressed this morning and the pill the manager reads this evening
 * are the same colour. The unnamed bucket is dashed and grey — it is history,
 * not somewhere money can be paid into.
 */
function AccountPill({ slot, index, currency }) {
  const named = Boolean(slot.label);
  const tint = tintFor(index);

  return (
    <span
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 9,
        padding: '8px 13px',
        borderRadius: T.radiusPill,
        background: 'rgba(255,255,255,0.08)',
        border: `1px ${named ? 'solid' : 'dashed'} ${T.headerBorder}`,
      }}
    >
      <span
        style={{
          width: 7,
          height: 7,
          borderRadius: T.radiusPill,
          background: named ? tint.ink : T.onHeaderFaint,
        }}
      />
      <span style={{ fontSize: 12.5, fontWeight: 600, color: T.onHeaderSoft }}>
        {named ? slot.label : COPY.unassigned}
      </span>
      <span style={{ fontSize: 13.5, fontWeight: 700, color: T.onHeader, ...NUMERIC }}>
        {money(slot.amount, currency)}
      </span>
      <span style={{ fontSize: 11, color: T.onHeaderFaint, ...NUMERIC }}>· {slot.count}</span>
    </span>
  );
}

export default function OrdersSummaryBand({
  summary,
  orderCount,
  range,
  currency,
  onOpenDatePicker,
  onExport,
  isExporting,
  dateAnchorRef,
  accountsOpen,
  onToggleAccounts,
}) {
  const accounts = summary.accounts || [];

  return (
    <section
      style={{
        flexShrink: 0,
        background: T.header,
        borderRadius: T.radiusPanel,
        overflow: 'hidden',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'stretch' }}>
      {/* Date control — borderless, because a bordered pill inside a card
          reads as a box in a box. The icon and chevron carry the affordance. */}
      <button
        type="button"
        ref={dateAnchorRef}
        onClick={onOpenDatePicker}
        style={{
          width: 216,
          flexShrink: 0,
          padding: '16px 18px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'flex-start',
          gap: 4,
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          font: 'inherit',
          textAlign: 'left',
        }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <CalendarIcon size={15} color={T.onHeaderFaint} />
          <span style={{ fontSize: 13.5, color: T.onHeader, fontWeight: 500, whiteSpace: 'nowrap' }}>
            {formatRange(range)}
          </span>
          <ChevronDownIcon size={16} color={T.onHeaderFaint} width={1.8} />
        </span>
        <span style={{ fontSize: 11.5, color: T.onHeaderFaint, paddingLeft: 23 }}>
          {COPY.orders(orderCount)}
        </span>
      </button>

      <div style={{ width: 1, background: T.headerBorder }} />

      <div
        style={{
          flex: 1,
          minWidth: 0,
          padding: '14px 20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 20,
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <span
            style={{
              fontSize: 27,
              fontWeight: 700,
              color: T.onHeader,
              letterSpacing: '-0.8px',
              lineHeight: 1.1,
              ...NUMERIC,
            }}
          >
            {money(summary.total, currency)}
          </span>
          {summary.discount > 0 && (
            <span style={{ fontSize: 11.5, color: T.onHeaderFaint, fontWeight: 500 }}>
              {COPY.discount}{' '}
              <span style={{ color: T.headerDanger, fontWeight: 700, ...NUMERIC }}>
                −{money(summary.discount, currency)}
              </span>
            </span>
          )}
        </div>

        <div style={{ display: 'flex', gap: 34 }}>
          <Slot label={COPY.cash} amount={summary.cash} dot={T.headerCash} currency={currency} />
          <Slot label={COPY.invoice} amount={summary.invoice} dot={T.headerInvoice} currency={currency} />
          <Slot label={COPY.pending} amount={summary.pending} dot={T.headerPending} currency={currency} />
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '0 18px 0 0' }}>
        <button
          type="button"
          onClick={onExport}
          disabled={isExporting || orderCount === 0}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 7,
            border: `1px solid ${T.headerControlBorder}`,
            borderRadius: T.radiusControl,
            padding: '8px 13px',
            background: 'transparent',
            cursor: orderCount === 0 ? 'not-allowed' : 'pointer',
            opacity: isExporting || orderCount === 0 ? 0.5 : 1,
            font: 'inherit',
            whiteSpace: 'nowrap',
          }}
        >
          <DownloadIcon size={15} color={T.onHeader} />
          <span style={{ fontSize: 12.5, color: T.onHeader, fontWeight: 600 }}>
            {COPY.exportPdf}
          </span>
        </button>

        {/* Folds the accounts away. With them hidden the band is exactly the
            height it has always been, so nobody who does not care about the
            split pays for it. */}
        {accounts.length ? (
          <button
            type="button"
            onClick={onToggleAccounts}
            title={accountsOpen ? COPY.hideAccounts : COPY.showAccounts}
            aria-expanded={accountsOpen}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 30,
              height: 30,
              border: `1px solid ${T.headerControlBorder}`,
              borderRadius: T.radiusControl,
              background: 'transparent',
              cursor: 'pointer',
              transform: accountsOpen ? 'rotate(180deg)' : 'none',
              transition: 'transform 0.18s ease',
            }}
          >
            <ChevronDownIcon size={16} color={T.onHeader} width={1.8} />
          </button>
        ) : null}
      </div>
      </div>

      {/* ── where the cash landed ──────────────────────────────────────────
          Only settled cash appears here, which is why it can be less than the
          total: a pending order is money nobody has yet. */}
      {accounts.length && accountsOpen ? (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            flexWrap: 'wrap',
            padding: '12px 20px 14px',
            borderTop: `1px solid ${T.headerBorder}`,
          }}
        >
          {accounts.map((slot, index) => (
            <AccountPill
              key={slot.label || 'unassigned'}
              slot={slot}
              index={index}
              currency={currency}
            />
          ))}
          <span style={{ fontSize: 11.5, color: T.onHeaderFaint, marginLeft: 'auto' }}>
            {COPY.cashSplit}
          </span>
        </div>
      ) : null}
    </section>
  );
}

/**
 * A waiter's header: the date, nothing else. Their range is locked to today
 * and they must not see shop-wide revenue.
 */
export function DateStrip({ range, orderCount }) {
  return (
    <div
      style={{
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        gap: 9,
        padding: '0 2px',
      }}
    >
      <CalendarIcon size={15} color={T.inkFaint} />
      <span style={{ fontSize: 13, fontWeight: 600, color: T.inkSoft }}>
        {formatRange(range)}
      </span>
      <span style={{ fontSize: 12, color: T.inkFaint }}>· {COPY.orders(orderCount)}</span>
    </div>
  );
}
